
import { defineComponent, computed, onMounted, ref, onUnmounted, ComponentPublicInstance } from "vue";
import {
  FridgeDetailsEconaviInfo,
  FridgeDetailsEconaviInfoListItemTime
} from "@/types/fridgeDetails";

import NativeClient, { TitleInfo } from "@/client/native-client";
import store from "@/store";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import { COMMON_LIMIT_NUM } from "@/const/limit_const";
import { COMMON_MESSAGE_STRING } from "@/const/message_const";
import CommonUtil, { translateYYYYMMDDToMDJapan } from "@/utils/common-utils";
import FridgeDetailsSelectDateBar from "@/components/fridgeDetails/FridgeDetailsSelectDateBar.vue";
import FridgeEconaviContentItem from "@/components/fridgeDetails/FridgeEconaviContentItem.vue";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";
import HorizontalPaging from "@/components/common/HorizontalPaging.vue";

/** スクロールイベントのIF */
interface HTMLElementEvent<T extends HTMLElement> extends Event {
  target: T;
}

export default defineComponent({
  name: "FridgeEconaviPage",
  components: { FridgeDetailsSelectDateBar, FridgeEconaviContentItem, CommonDesignDialog, HorizontalPaging },
  setup() {
    const { setLogId, outputPageLogs } = useLog();

    // タイトル行の設定
    NativeClient.setTitle(new TitleInfo(false, "エコナビ運転率"));
    CommonUtil.scroll({ top: 0, left: 0 });

    // 定数
    const topMessage = "冷蔵室・冷凍室が「中」設定のときにエコナビ運転します。";

    // 選択中のIndex（日付）
    const selectIndex = ref(0);
    const initialPageIndex = ref(0);

    const isDisableButtonHidden = true;

    // 表示更新のためのリアクティブ
    const updateDisplayKey = ref(0);
    const updateDisplay = () => {
      if (updateDisplayKey.value == 100) {
        updateDisplayKey.value = 0;
      } else {
        updateDisplayKey.value++;
      }
    };

    // 一覧更新
    const getEconaviInfo = async () => {
      NativeClient.sendDebugLog("FridgeEconaviPage getEconaviInfo()");
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      await store.dispatch("fridgeDetails/getEconaviInfo");
      store.dispatch("common/stopLoading");
      //updateDisplay();

      // 画面更新（当日の画面を表示するため）
      const result: FridgeDetailsEconaviInfo | null = store.getters["fridgeDetails/getEconaviInfo"];
      if (result != null && result.daily.econavi_list.length > 0) {
        selectIndex.value =  result.daily.econavi_list.length - 1;
        initialPageIndex.value = result.daily.econavi_list.length - 1;
      }
    };

    // 日付情報
    const dateListLength = ref(0);
    const dateList = computed(() => {
      const result: FridgeDetailsEconaviInfo | null = store.getters["fridgeDetails/getEconaviInfo"];
      NativeClient.sendDebugLog("FridgeEconaviPage dateList result = " + JSON.stringify(result));
      if (result != null && result.daily != null && result.daily.econavi_list != null && result.daily.econavi_list.length > 0) {
        const dateList: Array<string> = [];
        for (const item of result.daily.econavi_list) {
          const dateString = translateYYYYMMDDToMDJapan(item.date);
          dateList.push(dateString);
        }
        dateListLength.value = result.daily.econavi_list.length;
        return dateList;
      }
      NativeClient.sendDebugLog("FridgeEconaviPage dateList no element");
      return [];
    });
    const dateType = 0;

    // グラフの情報
    const graphInfoList = computed(() => {
      const result: FridgeDetailsEconaviInfo | null = store.getters["fridgeDetails/getEconaviInfo"];
      NativeClient.sendDebugLog("FridgeEconaviPage graphInfoList result = " + JSON.stringify(result));
      if (result != null && result.daily != null && result.daily.econavi_list != null) {
        const graphInfoList: Array<Array<FridgeDetailsEconaviInfoListItemTime>> = [];
        return result.daily.econavi_list;
      }
      NativeClient.sendDebugLog("FridgeEconaviPage graphInfoList no element");
      return [];
    });

    // スクロール
    const selectedDateIndex = (index: number) => {
      NativeClient.sendDebugLog("FridgeEconaviPage selectedDateIndex index = " + index);
      selectIndex.value = index;
    };

    const selectPage = (pageIndex: number) => {
      selectIndex.value = pageIndex;
    };

    // 表示の制御
    //let isDisplayed = false;
    onMounted(() => {
      NativeClient.sendDebugLog("FridgeEconaviPage onMounted()");

      // ページIDは未決定
      // const logInfo = new BeaconLogInfo(PAGE_ID.FAVORITES_MYLIST, null, []);
      // NativeClient.sendViewedLog(logInfo);

      getEconaviInfo();
    });
    onUnmounted(() => {
      NativeClient.sendDebugLog("FridgeEconaviPage onUnmounted()");
    });

       //
    // APIリクエスト時のエラーハンドル
    //
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const typeApiError = ref(9);
    const retryButtonTitle = "リトライ";
    const okButtonTitle = "OK";
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const retryApiErrorHandler = () => {
      closeDialog();
      // getEconaviInfo();
    };
    const okApiErrorHandler = () => {
      closeDialog();
    };

    return {
      topMessage,
      isDisableButtonHidden,
      selectIndex,
      initialPageIndex,
      //touchStart,
      //touchMove,
      //touchEnd,
      dateList,
      graphInfoList,
      dateType,
      scroll,
      //onScroll,
      updateDisplayKey,
      selectedDateIndex,
      selectPage,
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      typeApiError,
      retryButtonTitle,
      okButtonTitle,
      retryApiErrorHandler,
      okApiErrorHandler
    };
  }
});
